/**
 * SEOHead - runtime injector of CMS-managed SEO tags for the current route.
 *
 * Behaviour
 *   - Fetches /api/seo/page?path=<current pathname> once per unique path,
 *     caches in-memory, and swaps out on route changes.
 *   - Emits <title>, <meta description/keywords/robots>, canonical link,
 *     Open Graph + Twitter tags, and JSON-LD (auto Organization + WebSite
 *     + BreadcrumbList; admin custom override wins if provided).
 *   - Page-level <Helmet> blocks in individual pages still work: this
 *     component is rendered LAST in the tree so its declarations win when
 *     admins have populated a per-page record. When they haven't, the page's
 *     own Helmet tags remain in place unchanged.
 */
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { api, mediaUrl } from "../lib/api";

const cache = new Map(); // path -> resolved SEO record

/**
 * Neutralize the three sequences a JSON string can smuggle to break out of a
 * <script type="application/ld+json"> container:
 *   - "</" would close the script tag early
 *   - "<!--" / "-->" open/close HTML comment blocks the parser will honor
 *   - "<script" would nest a new script element
 * JSON syntax allows the backslash-escaped form of "<" ("\u003c") so the
 * output remains valid JSON-LD to consumers while being inert to the HTML
 * parser. This is the standard mitigation recommended by the JSON-in-HTML
 * cheat sheet.
 */
function safeJsonForScript(raw) {
  if (typeof raw !== "string") return "";
  return raw
    .replace(/<\/(script)/gi, "<\\/$1")
    .replace(/<!--/g, "\\u003c!--")
    .replace(/<script/gi, "\\u003cscript");
}

async function fetchSEO(path) {
  if (cache.has(path)) return cache.get(path);
  try {
    const { data } = await api.get("/seo/page", { params: { path } });
    cache.set(path, data);
    return data;
  } catch {
    return null;
  }
}

function buildBreadcrumbs(pathname, baseUrl, siteName) {
  const clean = (baseUrl || "").replace(/\/$/, "");
  const parts = pathname.split("/").filter(Boolean);
  const items = [{ name: siteName || "Home", url: `${clean}/` }];
  let acc = "";
  parts.forEach((seg) => {
    acc += `/${seg}`;
    items.push({
      name: seg.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
      url: `${clean}${acc}`,
    });
  });
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: it.url,
    })),
  };
}

function buildOrganization(seo) {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: seo.organization_name || seo.site_name,
    url: (seo.canonical_base_url || "").replace(/\/$/, "") + "/",
    logo: seo.organization_logo_url || seo.og_image_url,
    sameAs: seo.organization_same_as || [],
  };
}

export default function SEOHead() {
  const { pathname } = useLocation();
  const [seo, setSeo] = useState(null);

  useEffect(() => {
    let alive = true;
    fetchSEO(pathname).then((data) => { if (alive) setSeo(data); });
    return () => { alive = false; };
  }, [pathname]);

  if (!seo) return null;

  const breadcrumb = buildBreadcrumbs(pathname, seo.canonical_base_url, seo.site_name);
  const customSite = (seo.custom_site_schema || "").trim();
  const orgJsonLd = safeJsonForScript(customSite || JSON.stringify(buildOrganization(seo)));
  const breadcrumbJsonLd = safeJsonForScript(JSON.stringify(breadcrumb));

  // Case A: page has NO admin-curated record. Emit only "safe" baseline tags
  // (canonical, robots, site-wide OG/Twitter, JSON-LD) so we never clobber a
  // page's own <Helmet> title/description block for un-managed pages.
  if (!seo.has_record) {
    return (
      <Helmet>
        <link rel="canonical" href={seo.canonical_url} />
        <meta name="robots" content={seo.robots || "index, follow"} />
        <meta property="og:site_name" content={seo.site_name || "T-REX Network"} />
        {seo.twitter_handle ? <meta name="twitter:site" content={seo.twitter_handle} /> : null}
        <script type="application/ld+json">{breadcrumbJsonLd}</script>
        <script type="application/ld+json">{orgJsonLd}</script>
      </Helmet>
    );
  }

  // Case B: admin has curated this page. Emit the full override set.
  const ogImage = seo.og_image_media_id ? mediaUrl(seo.og_image_media_id) : seo.og_image_url;
  const customPage = safeJsonForScript((seo.schema_json || "").trim());

  return (
    <Helmet prioritizeSeoTags>
      <title>{seo.title}</title>
      <meta name="description" content={seo.description || ""} />
      {seo.keywords ? <meta name="keywords" content={seo.keywords} /> : null}
      <meta name="robots" content={seo.robots || "index, follow"} />
      <link rel="canonical" href={seo.canonical_url} />

      {/* Open Graph */}
      <meta property="og:site_name" content={seo.site_name || "T-REX Network"} />
      <meta property="og:title" content={seo.og_title || seo.title} />
      <meta property="og:description" content={seo.og_description || seo.description || ""} />
      <meta property="og:url" content={seo.canonical_url} />
      {ogImage ? <meta property="og:image" content={ogImage} /> : null}
      <meta property="og:type" content="website" />

      {/* Twitter */}
      <meta name="twitter:card" content={seo.twitter_card || "summary_large_image"} />
      {seo.twitter_handle ? <meta name="twitter:site" content={seo.twitter_handle} /> : null}
      <meta name="twitter:title" content={seo.og_title || seo.title} />
      <meta name="twitter:description" content={seo.og_description || seo.description || ""} />
      {ogImage ? <meta name="twitter:image" content={ogImage} /> : null}

      {/* JSON-LD */}
      <script type="application/ld+json">{breadcrumbJsonLd}</script>
      <script type="application/ld+json">{orgJsonLd}</script>
      {customPage ? <script type="application/ld+json">{customPage}</script> : null}
    </Helmet>
  );
}

// Exposed so admins clearing a record can wipe the cached entry.
export function invalidateSEOCache(path) {
  if (!path) cache.clear();
  else cache.delete(path);
}
